import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getBackendUrl } from '../utils/api';

const AccessContext = createContext(null);

// LocalStorage keys for caching
const ACCESS_CACHE_KEY = 'radio_access_cache';
const ACCESS_CACHE_TIMESTAMP_KEY = 'radio_access_cache_timestamp';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour cache validity

// Loading states for user feedback
export const LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SLOW: 'slow',
  RETRYING: 'retrying',
  USING_CACHE: 'using_cache',
  ERROR: 'error'
};

// Feature to route mapping
const FEATURE_ROUTES = {
  categories_on_air: ['/categories', '/themes'],
  campaign_analyzer: ['/metritis-kampanias', '/campaign'],
  listener_calculator: ['/metritis-akroaton', '/listener'],
  radio_top_songs: ['/top-songs', '/radio-top', '/radio-top-songs'],
  peoples_chart: ['/radio-chart'],
  compare_stations: ['/compare-stations'],
};

// Feature names in Greek
const FEATURE_NAMES = {
  categories_on_air: 'Κατηγορίες Θεμάτων On Air',
  campaign_analyzer: 'Αναλυτής Καμπάνιας',
  listener_calculator: 'Μετρητής Ακροατών',
  radio_top_songs: 'Radio Top Songs',
  monthly_support: 'Μηνιαία Υποστήριξη',
  priority_support: 'Προτεραιότητα Υποστήριξης',
  peoples_chart: "The People's Chart",
  compare_stations: 'Compare Stations',
};

export const AccessProvider = ({ children, user }) => {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingState, setLoadingState] = useState(LOADING_STATES.IDLE);
  const [retryCount, setRetryCount] = useState(0);
  
  const backendUrl = getBackendUrl();

  // Load cached access on mount
  useEffect(() => {
    const userId = user?.user_id || user?.id;
    if (userId) {
      try {
        const cachedData = localStorage.getItem(ACCESS_CACHE_KEY);
        const cachedTimestamp = localStorage.getItem(ACCESS_CACHE_TIMESTAMP_KEY);
        
        if (cachedData && cachedTimestamp) {
          const cached = JSON.parse(cachedData);
          const timestamp = parseInt(cachedTimestamp, 10);
          const now = Date.now();
          
          // Check if cache is for the same user and not expired
          if (cached.userId === userId && (now - timestamp) < CACHE_TTL_MS) {
            console.log('🔐 [AccessContext] Using cached access data');
            setAccess(cached.access);
            setLoading(false);
          }
        }
      } catch (e) {
        console.warn('🔐 [AccessContext] Failed to load cache:', e);
      }
    }
  }, [user]);

  // Save access to cache
  const saveToCache = useCallback((userId, accessData) => {
    try {
      localStorage.setItem(ACCESS_CACHE_KEY, JSON.stringify({
        userId,
        access: accessData
      }));
      localStorage.setItem(ACCESS_CACHE_TIMESTAMP_KEY, Date.now().toString());
      console.log('🔐 [AccessContext] Access cached successfully');
    } catch (e) {
      console.warn('🔐 [AccessContext] Failed to cache access:', e);
    }
  }, []);

  const fetchAccess = useCallback(async (signal) => {
    // Check for user_id or id (backwards compatibility)
    const userId = user?.user_id || user?.id;
    console.log('🔐 [AccessContext] Fetching access for user:', userId, 'user object:', user);
    
    if (!userId) {
      console.log('🔐 [AccessContext] No userId found, setting access to null');
      setAccess(null);
      setLoading(false);
      setLoadingState(LOADING_STATES.IDLE);
      return;
    }

    setLoading(true);
    setLoadingState(LOADING_STATES.LOADING);
    setRetryCount(0);
    
    // Set slow connection warning after 8 seconds (increased for cold start scenarios)
    // First connection to MongoDB Atlas can take 4-6 seconds
    const slowTimeout = setTimeout(() => {
      setLoadingState(LOADING_STATES.SLOW);
    }, 8000);
    
    // Retry logic - try up to 3 times with delay
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const url = `${backendUrl}/api/access/${userId}`;
        console.log(`🔐 [AccessContext] Calling API (attempt ${attempt}):`, url);
        
        if (attempt > 1) {
          setLoadingState(LOADING_STATES.RETRYING);
          setRetryCount(attempt);
        }
        
        const response = await fetch(url, { signal });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        console.log('🔐 [AccessContext] API response:', data);
        clearTimeout(slowTimeout);
        setAccess(data);
        saveToCache(userId, data); // Cache the result
        setLoading(false);
        setLoadingState(LOADING_STATES.IDLE);
        return; // Success - exit the retry loop
        
      } catch (error) {
        lastError = error;
        
        // Don't retry if request was aborted (component unmounted)
        if (error.name === 'AbortError') {
          console.log('🔐 [AccessContext] Request aborted (component unmounted)');
          clearTimeout(slowTimeout);
          return;
        }
        
        console.warn(`🔐 [AccessContext] Attempt ${attempt} failed:`, error.message);
        
        // Wait before retrying (exponential backoff)
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, attempt * 500));
        }
      }
    }
    
    clearTimeout(slowTimeout);
    console.error('🔐 [AccessContext] All attempts failed:', lastError);
    
    // Try to use cached data as fallback
    try {
      const cachedData = localStorage.getItem(ACCESS_CACHE_KEY);
      if (cachedData) {
        const cached = JSON.parse(cachedData);
        if (cached.userId === userId) {
          console.log('🔐 [AccessContext] Using stale cache as fallback');
          setAccess(cached.access);
          setLoading(false);
          setLoadingState(LOADING_STATES.USING_CACHE);
          return;
        }
      }
    } catch (e) {
      console.warn('🔐 [AccessContext] Fallback cache failed:', e);
    }
    
    setAccess(null);
    setLoading(false);
    setLoadingState(LOADING_STATES.ERROR);
  }, [user, backendUrl, saveToCache]);

  useEffect(() => {
    console.log('🔐 [AccessContext] User changed, fetching access. User:', user?.username || 'null');
    
    // Create AbortController for cleanup
    const abortController = new AbortController();
    
    fetchAccess(abortController.signal);
    
    // Cleanup function - abort fetch if component unmounts or user changes
    return () => {
      abortController.abort();
    };
  }, [fetchAccess]);

  // Check if user has access to a specific feature
  const hasFeatureAccess = useCallback((featureId) => {
    console.log('🔐 [hasFeatureAccess] Checking feature:', featureId, 'access:', access, 'loading:', loading);
    
    // If still loading, return true temporarily to prevent UI flash
    // The FeatureGate component will show loading state instead
    if (loading) {
      console.log('🔐 [hasFeatureAccess] Still loading, showing loading state');
      return true; // Return true while loading to prevent locked state
    }
    
    if (!access) {
      // Only log error if not loading and user exists
      if (user) {
        console.warn('🔐 [hasFeatureAccess] ⚠️ No access object but user exists - may be fetching');
      }
      return false;
    }
    
    // Admin and trial have full access to everything
    if (['admin', 'trial'].includes(access.access_type)) {
      console.log('🔐 [hasFeatureAccess] ✅ Full access granted (type:', access.access_type, ')');
      return true;
    }
    
    // free_access and subscribers check their features list
    const hasFeature = access.features?.includes(featureId) || false;
    console.log('🔐 [hasFeatureAccess] Feature check:', featureId, hasFeature, '(type:', access.access_type, ')');
    return hasFeature;
  }, [access, loading, user]);

  // Check if user can access a specific route
  const canAccessRoute = useCallback((route) => {
    if (!access) return false;
    
    // Admin and trial have full access
    if (['admin', 'trial'].includes(access.access_type)) {
      return true;
    }
    
    // free_access and subscribers: check which feature this route belongs to
    for (const [featureId, routes] of Object.entries(FEATURE_ROUTES)) {
      if (routes.some(r => route.startsWith(r))) {
        return access.features?.includes(featureId) || false;
      }
    }
    
    // Default: allow access (for routes not tied to features)
    return true;
  }, [access]);

  // Get list of locked features
  const getLockedFeatures = useCallback(() => {
    if (!access) return Object.keys(FEATURE_NAMES);
    
    // Admin and trial have no locked features
    if (['admin', 'trial'].includes(access.access_type)) {
      return [];
    }
    
    const allFeatures = Object.keys(FEATURE_NAMES);
    const userFeatures = access.features || [];
    
    return allFeatures.filter(f => !userFeatures.includes(f));
  }, [access]);

  // Check if user has full access (admin/trial/free)
  const hasFullAccess = useCallback(() => {
    return ['admin', 'trial'].includes(access?.access_type);
  }, [access]);

  const value = {
    access,
    loading,
    loadingState,
    retryCount,
    hasFeatureAccess,
    canAccessRoute,
    getLockedFeatures,
    hasFullAccess,
    refreshAccess: fetchAccess,
    FEATURE_NAMES,
    FEATURE_ROUTES,
    LOADING_STATES,
  };

  return (
    <AccessContext.Provider value={value}>
      {children}
    </AccessContext.Provider>
  );
};

export const useAccess = () => {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error('useAccess must be used within AccessProvider');
  }
  return context;
};

// HOC to protect components based on feature access
export const withFeatureAccess = (WrappedComponent, featureId) => {
  return function ProtectedComponent(props) {
    const { hasFeatureAccess, access, loading } = useAccess();
    
    if (loading) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      );
    }
    
    if (!hasFeatureAccess(featureId)) {
      return (
        <div className="bg-gradient-to-br from-[#2D0C4B] via-[#4A1D7A] to-[#6B3FA0] min-h-[400px] flex items-center justify-center p-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-md text-center">
            <div className="text-5xl mb-4">🔒</div>
            <h3 className="text-xl font-bold text-white mb-2">
              Απαιτείται Αναβάθμιση
            </h3>
            <p className="text-white/70 mb-4">
              Αυτή η λειτουργία δεν είναι διαθέσιμη στο τρέχον πακέτο σας.
            </p>
            <p className="text-white/50 text-sm mb-6">
              {access?.message}
            </p>
            <a 
              href="/pricing"
              className="inline-block bg-gradient-to-r from-[#FF9F0A] to-[#FFB84D] text-[#2D0C4B] font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
            >
              Δείτε τα Πακέτα
            </a>
          </div>
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
};

export default AccessContext;
